import { useCallback, useEffect, useRef, useState } from 'react';
import { useCheckAsync } from '../../hooks';
import { type PreviewId } from '../types';
import {
    getLayers,
    getQueueToClose,
    onlyOneWidgetOpen,
    previewLayerState,
    removeClosedWidgets,
} from './previewLayerState';
import { usePreviewWidget } from './usePreviewWidget';

export function usePreviewQueue() {
    const { isOpen, id, data, setClose } = usePreviewWidget();

    // eslint-disable-next-line react-hooks/purity -- Автоматический игнор в рамках DIRECT-288723
    const [_, setTimestamp] = useState(Date.now());
    const checkAsync = useCheckAsync();

    // Если есть id - значит надо показывать виджет
    if (id) {
        // Если id нет в очереди на показ
        if (previewLayerState.queue.has(id)) {
            // Если id есть в очереди на показ
            // Закрываем все виджеты кроме этого
            onlyOneWidgetOpen(id);
            // Ставим его в очереди на последнее место
            previewLayerState.queue.delete(id);
            previewLayerState.queue.add(id);
        } else {
            // Закрываем все виджеты
            onlyOneWidgetOpen();
            // Добавляем id в очередь на показ
            previewLayerState.queue.add(id);
            previewLayerState.layers.set(id, { isOpen, id, data });
        }
    } else {
        onlyOneWidgetOpen();
    }

    useEffect(() => {
        const toClose = getQueueToClose();
        const marker = checkAsync.current.save('useEffect');

        if (toClose.length) {
            setTimeout(() => {
                if (!checkAsync.current.check('useEffect', marker)) {
                    return;
                }

                removeClosedWidgets(toClose);
                setTimestamp(Date.now());
            }, 300);
        }
    });

    const refId = useRef(id);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-312943
    refId.current = id;

    const timeoutClose = useCallback(
        // eslint-disable-next-line react-hooks/preserve-manual-memoization -- Автоматический игнор в рамках DIRECT-312943
        (closedId: PreviewId) => {
            if (refId.current !== closedId) {
                return;
            }

            const marker = checkAsync.current.save();

            setTimeout(() => {
                if (refId.current !== closedId || !checkAsync.current.check(marker)) {
                    return;
                }

                setClose();
            }, 100);
        },
        [checkAsync, setClose]
    );

    return {
        layers: getLayers(),
        setClose: timeoutClose,
    };
}
